'use client'

import { type PropsWithChildren } from 'react'
import { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'
import { assertEvent, assign, createMachine, fromCallback } from 'xstate'

import { createActorContext } from '@/utils/createActorContext'
import { machineTypes } from '@/utils/machineTypes'

type ShowcaseContextValue = {
  index: number
  items: number
  options: EmblaOptionsType
  api: EmblaCarouselType | undefined
  standalone: boolean
  dimmed: boolean
}

const defaultContext: ShowcaseContextValue = {
  index: 0,
  items: 0,
  options: { containScroll: false },
  api: undefined,
  standalone: false,
  dimmed: false,
}

const showcaseMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5SwBYHsDuBjAhrMAxAOJoAEALmbADYCWEYA2gAwC6ioADmrLebWgB2HEAA9EAWgDMATgDsAOgCsUgIwAOVVIAsq7cwBsUgEwAaEAE9JGqQuOq5B5jOPH1BpczkBfb+dSYuPgKAJKCOFj8AG6EAIKRtFE45GCkAdh4TGwi3Lz8QiLiCBLGcup2LjJK2nLMxmrV5lYIaor2BqpGBsbaSj3Gvv7oGcFhEdGEACK0ALZpw0FZ7EgguXwCwitFEqqqxgrq-cZK8nIy7s5NiNraCgZySu7ahzKqVXuDIOmLCvETBJMwONEslUt9MixllweOsCltrEoDAo3up1HVtFJnMZ7mZLIglBoFJi3KipA9mOoVJ9wcEADJoHAQWiCKCkXAAJzQAFd8NRSLEAAohAgAZTA5FIzPWOD5sHIoMhORh+U2oG2jiRj3sulUznUcik6iuCGMMluzyqqJk9zO6hk1IWmQU9MZzNZHO5vP5QtF4slgmlsvlKUYqihq2VG0KkmqigNBLk2iMMikqdUxs6th6HQ8Dmz5ykDsCTr+IP4LIIojloIUOAAZil2QAKTzMACUBBpYF+CSS5agipWaxV0eKhv2zE6N2cbwN2h6xtkSgUzGYNxkKY3ejNRZG3cBwL7bsr1ZStYbYGbxlXHa7CgPveSbsH0LyUfhxV2y9crjJOjORyLpSK4OFI1RGMwmI6L4fggIIaAMPAKxdkqb5wmqkiUswyJOD0vQ9EohFSMaEjzgcPRaIiqYEvUAywXeYy9mAqGwqqYiSPUSL6PUninOcHQZsB8ilLILxvDRu4-KWMQsSOH4SGi+ydOi+G9ERxo3MuW6ePc1RYoW9GOnSDJMiybI4JyPJgHygohLJ74YcUchvAoNyqASez3LovTGvcrkqbUxzvG8kklo+-b2eh7HFLotg1Lqq6QXoKiUoudR2I8BgdJO7ieEooXBA+0RPiykVsdsWjYXs6gWoaa7MIiSgab0IHxkmUGQdoMHeEAA */
    id: 'showcase',
    types: machineTypes<{
      context: ShowcaseContextValue
      input: Partial<ShowcaseContextValue>
      events:
        | { type: 'Activate showcase' }
        | { type: 'Dim showcase'; dimmed: boolean }
        | { type: 'Go to slide'; index: number }
        | { type: 'Set initial state'; api: EmblaCarouselType }
        | { type: 'Deactivate showcase' }
    }>(),
    context: ({ input }) => ({
      ...defaultContext,
      ...input,
    }),
    initial: 'Loading carousel API',
    states: {
      Inactive: {
        on: {
          'Activate showcase': 'Activating',

          'Dim showcase': {
            target: 'Inactive',
            actions: 'Set dimmed',
          },
        },
      },

      Active: {
        on: {
          'Deactivate showcase': 'Deactivating',
        },

        invoke: [
          {
            src: 'Listen for escape key',
            id: 'escapeKeyListener',
          },
          {
            src: 'Listen for arrow keys',
            id: 'arrowKeyListener',
            input: ({ context }) => ({ api: context.api }),
          },
        ],
      },

      'Loading carousel API': {
        on: {
          'Set initial state': [
            {
              target: 'Active',
              actions: 'Save API',
              guard: 'Always active',
            },
            {
              target: 'Inactive',
              actions: 'Save API',
            },
          ],
        },
      },

      Activating: {
        after: {
          '500': 'Active',
        },
      },

      Deactivating: {
        after: {
          '200': 'Inactive',
        },
      },
    },
    on: {
      'Go to slide': {
        actions: ['Set index', 'Scroll to index'],
      },
    },
  },
  {
    guards: {
      'Always active': ({ context }) => context.standalone,
    },
    actions: {
      'Save API': assign(({ event }) => {
        assertEvent(event, 'Set initial state')

        return { api: event.api, items: event.api?.slideNodes().length || 0 }
      }),
      'Set index': assign(({ event }) => {
        assertEvent(event, 'Go to slide')

        return { index: event.index }
      }),
      'Scroll to index': ({ context, event }) => {
        assertEvent(event, 'Go to slide')

        context.api?.scrollTo(event.index)
      },
      'Set dimmed': assign(({ event }) => {
        assertEvent(event, 'Dim showcase')

        return { dimmed: event.dimmed }
      }),
    },
    actors: {
      'Listen for escape key': fromCallback(({ sendBack }) => {
        const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            sendBack({ type: 'Deactivate showcase' })
          }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
          document.removeEventListener('keydown', handleKeyDown)
        }
      }),
      'Listen for arrow keys': fromCallback<
        { type: 'Go to slide'; index: number },
        { api: EmblaCarouselType | undefined }
      >(({ sendBack, input: { api } }) => {
        const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'ArrowLeft' && api?.canScrollPrev()) {
            sendBack({ type: 'Go to slide', index: api.selectedScrollSnap() - 1 })
          } else if (event.key === 'ArrowRight' && api?.canScrollNext()) {
            sendBack({ type: 'Go to slide', index: api.selectedScrollSnap() + 1 })
          }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
          document.removeEventListener('keydown', handleKeyDown)
        }
      }),
    },
  },
)

export type ShowcaseMachine = typeof showcaseMachine

export const ShowcaseContext = createActorContext(showcaseMachine)

export function ShowcaseProvider({
  children,
  ...contextAsProps
}: PropsWithChildren<{ standalone?: boolean; dimmed?: boolean }>) {
  return (
    <ShowcaseContext.Provider options={{ input: { ...defaultContext, ...contextAsProps } }}>
      {children}
    </ShowcaseContext.Provider>
  )
}
